import ResourceModule, { type ResourceModuleConfig } from "@/pages/admin/components/ResourceModule";
import { StatusPill, type Column } from "@/pages/admin/components/AdminUI";
import { optionsFrom } from "@/lib/adminContent";
import {
  INCIDENT_SEVERITY,
  INCIDENT_STATUS,
  INCIDENT_TYPE,
  metaFor,
  type Incident,
} from "@/lib/adminSystem";
import { formatDate } from "@/pages/admin/format";

const columns: Column<Incident>[] = [
  {
    key: "title",
    header: "Incident",
    primary: true,
    render: (row) => (
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.title}</p>
        <p className="text-xs text-foreground-500 mt-0.5">
          {row.ref || "—"} · {row.farm_name || "—"}
        </p>
      </div>
    ),
  },
  { key: "type", header: "Type", render: (row) => <StatusPill {...metaFor(INCIDENT_TYPE, row.incident_type)} /> },
  { key: "severity", header: "Severity", render: (row) => <StatusPill {...metaFor(INCIDENT_SEVERITY, row.severity)} /> },
  { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(INCIDENT_STATUS, row.status)} /> },
  { key: "occurred", header: "Occurred", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.occurred_at)}</span> },
];

const config: ResourceModuleConfig<Incident> = {
  table: "incidents",
  orderBy: "occurred_at",
  eyebrow: "Safety & Compliance",
  title: "Incidents",
  subtitle: "Every incident and near-miss across the farm network — logged, investigated and closed with the action taken on record.",
  searchPlaceholder: "Search by title, reference, farm…",
  searchKeys: ["ref", "title", "farm_name", "reported_by", "description"],
  filters: [
    { key: "status", label: "Status", options: optionsFrom(INCIDENT_STATUS) },
    { key: "severity", label: "Severity", options: optionsFrom(INCIDENT_SEVERITY) },
    { key: "incident_type", label: "Type", options: optionsFrom(INCIDENT_TYPE) },
  ],
  statusMap: INCIDENT_STATUS,
  statusKey: "status",
  stats: (rows) => [
    { label: "Total incidents", value: rows.length, icon: "ri-alert-line", tone: "primary" },
    { label: "Open", value: rows.filter((row) => row.status === "open" || row.status === "in_progress").length, icon: "ri-error-warning-line", tone: "accent" },
    { label: "High / critical", value: rows.filter((row) => row.severity === "high" || row.severity === "critical").length, icon: "ri-alarm-warning-line", tone: "accent" },
    { label: "Resolved", value: rows.filter((row) => row.status === "resolved" || row.status === "closed").length, icon: "ri-check-double-line", tone: "primary" },
  ],
  columns,
  fields: [
    { key: "ref", label: "Reference" },
    { key: "title", label: "Title" },
    { key: "farm_name", label: "Farm" },
    { key: "incident_type", label: "Type", type: "select", options: optionsFrom(INCIDENT_TYPE) },
    { key: "severity", label: "Severity", type: "select", options: optionsFrom(INCIDENT_SEVERITY) },
    { key: "status", label: "Status", type: "select", options: optionsFrom(INCIDENT_STATUS) },
    { key: "reported_by", label: "Reported by" },
    { key: "occurred_at", label: "Occurred on", type: "date" },
    { key: "description", label: "What happened", type: "textarea" },
    { key: "action_taken", label: "Action taken", type: "textarea" },
  ],
  createFields: [
    { key: "ref", label: "Reference" },
    { key: "title", label: "Title" },
    { key: "farm_name", label: "Farm" },
    { key: "incident_type", label: "Type", type: "select", options: optionsFrom(INCIDENT_TYPE) },
    { key: "severity", label: "Severity", type: "select", options: optionsFrom(INCIDENT_SEVERITY) },
    { key: "reported_by", label: "Reported by" },
    { key: "description", label: "What happened", type: "textarea" },
  ],
  newDefaults: {
    ref: "",
    title: "",
    farm_name: "",
    incident_type: "minor",
    severity: "low",
    status: "open",
    reported_by: "",
    description: "",
    action_taken: "",
  },
  emptyTitle: "No incidents logged",
  emptyMessage: "Incidents and near-misses reported by hosts will appear here for review.",
  rowTitle: (row) => row.title,
};

export default function AdminIncidents() {
  return <ResourceModule config={config} />;
}